import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Target, Plus, Trash2, CalendarDays, ClipboardList } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusChip } from "@/components/StatusChip";
import { SpecificityField, PlainDateField } from "@/components/SpecificityField";
import { ApprovalResult, ApprovalActions } from "@/components/case/EntryRequestSection";
import { formatDate, formatDateTime } from "@/lib/format";
import { specificityCheck } from "@/lib/format";

const uid = () => Math.random().toString(36).slice(2, 10);

function PlanView({ plan }) {
  return (
    <div className="space-y-4" data-testid="plan-view">
      <div className="flex flex-wrap gap-2">
        <StatusChip label={`${plan.duration}-day plan`} tone="green" />
        <span className="text-xs text-patria-muted rounded-full bg-patria-sage/60 px-2.5 py-1 inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {formatDate(plan.start_date)} → {formatDate(plan.review_date)}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><p className="text-xs font-semibold text-patria-muted uppercase tracking-wide">The gap</p><p className="text-sm text-patria-ink mt-1 whitespace-pre-wrap">{plan.gap}</p></div>
        <div><p className="text-xs font-semibold text-patria-muted uppercase tracking-wide">Required standard</p><p className="text-sm text-patria-ink mt-1 whitespace-pre-wrap">{plan.standard}</p></div>
        <div className="sm:col-span-2"><p className="text-xs font-semibold text-patria-muted uppercase tracking-wide">Support & resources</p><p className="text-sm text-patria-ink mt-1 whitespace-pre-wrap">{plan.resources}</p></div>
      </div>
      <div>
        <p className="text-xs font-semibold text-patria-muted uppercase tracking-wide mb-2">Goals & objective measures</p>
        <div className="space-y-2">
          {plan.goals.map((g, i) => (
            <div key={i} className="rounded-xl bg-patria-sage/40 p-3" data-testid={`plan-goal-${i}`}>
              <p className="text-sm font-medium text-patria-ink">{i + 1}. {g.expectation}</p>
              <p className="text-sm text-patria-muted mt-0.5">Measure: {g.measure}</p>
              <p className="text-xs text-patria-muted mt-0.5">Deadline: {formatDate(g.deadline)}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-patria-muted uppercase tracking-wide mb-1">Scheduled check-ins</p>
        <div className="flex flex-wrap gap-2">
          {plan.scheduled_checkins?.map((sc, i) => (
            <span key={i} className={`text-xs rounded-full px-2.5 py-1 ${sc.completed ? "bg-patria-green/12 text-patria-greenDeep line-through" : "bg-patria-sage/60 text-patria-muted"}`}>{formatDate(sc.date)}</span>
          ))}
        </div>
      </div>
      <p className="text-xs text-patria-muted">Built by {plan.built_by?.name} · submitted {formatDateTime(plan.submitted_at)}</p>
    </div>
  );
}

function PlanBuilder({ caseId, feedback }) {
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const [duration, setDuration] = useState(60);
  const [startDate, setStartDate] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [gap, setGap] = useState(feedback?.gap || "");
  const [standard, setStandard] = useState(feedback?.standard || "");
  const [resources, setResources] = useState("");
  const [goals, setGoals] = useState([{ _id: uid(), expectation: "", measure: "", deadline: "" }]);
  const [checkins, setCheckins] = useState([{ _id: uid(), value: "" }]);

  const submit = useMutation({
    mutationFn: async () => api.post(`/cases/${caseId}/plan`, {
      duration, start_date: startDate, review_date: reviewDate, gap, standard, resources,
      goals: goals.map(({ expectation, measure, deadline }) => ({ expectation, measure, deadline })),
      checkin_dates: checkins.map((c) => c.value).filter(Boolean),
    }),
    onSuccess: () => { toast.success("Plan submitted for People Operations approval"); qc.invalidateQueries({ queryKey: ["case", caseId] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const goalsValid = goals.every((g) => g.expectation.trim().length >= 8 && g.measure.trim().length >= 10 && g.deadline);
  const valid = startDate && reviewDate && specificityCheck(gap, { minLen: 40, requireDate: true }).ok && specificityCheck(standard, { minLen: 25 }).ok && specificityCheck(resources, { minLen: 15 }).ok && goalsValid && checkins.filter((c) => c.value).length > 0;

  return (
    <div className="space-y-5" data-testid="plan-builder">
      <div className="space-y-2">
        <Label className="text-patria-ink font-medium">Duration</Label>
        <div className="flex flex-wrap gap-2">
          {[30, 60, 90].map((d) => (
            <button key={d} type="button" onClick={() => setDuration(d)} data-testid={`plan-duration-${d}`}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${duration === d ? "border-patria-green bg-patria-green/8" : "border-patria-muted/25 bg-patria-surface hover:border-patria-green/40"}`}>
              <p className="font-heading font-semibold text-patria-ink">{d} days</p>
              <p className="text-xs text-patria-muted max-w-[180px]">{meta?.duration_guidance?.[String(d)]}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <PlainDateField label="Start date" value={startDate} onChange={setStartDate} testId="plan-start-date" />
        <PlainDateField label="Final-review date" value={reviewDate} onChange={setReviewDate} testId="plan-review-date" />
      </div>

      <SpecificityField label="Restate the specific gap" value={gap} onChange={setGap} minLen={40} requireDate helper="Carry forward the documented gap — specific, with dates." testId="plan-gap" />
      <SpecificityField label="The required standard" value={standard} onChange={setStandard} minLen={25} helper="Objective, tied to the job description." testId="plan-standard" />
      <SpecificityField label="Support & resources Patria will provide" value={resources} onChange={setResources} minLen={15} helper="Coaching, tools, training, or schedule support." testId="plan-resources" />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-patria-ink font-medium">Goals — each needs an objective measure & deadline</Label>
          <Button type="button" size="sm" variant="ghost" onClick={() => setGoals((g) => [...g, { _id: uid(), expectation: "", measure: "", deadline: "" }])} className="text-patria-green" data-testid="plan-add-goal"><Plus className="h-4 w-4 mr-1" /> Add goal</Button>
        </div>
        <div className="space-y-3">
          {goals.map((g, i) => {
            const measureOk = specificityCheck(g.measure, { minLen: 10 });
            return (
              <div key={g._id} className="rounded-xl border border-patria-muted/20 bg-patria-sage/30 p-3 space-y-2.5" data-testid={`plan-goal-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-patria-muted">Goal {i + 1}</span>
                  {goals.length > 1 && <button type="button" onClick={() => setGoals((arr) => arr.filter((_, j) => j !== i))} className="text-patria-terracottaDeep" data-testid={`plan-remove-goal-${i}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
                <Input value={g.expectation} onChange={(e) => setGoals((arr) => arr.map((x, j) => (j === i ? { ...x, expectation: e.target.value } : x)))} placeholder="Goal / expectation" data-testid={`plan-goal-expectation-${i}`} className="bg-patria-surface border-patria-muted/30" />
                <Input value={g.measure} onChange={(e) => setGoals((arr) => arr.map((x, j) => (j === i ? { ...x, measure: e.target.value } : x)))} placeholder="How success is measured (objective, not impression)" data-testid={`plan-goal-measure-${i}`} className={`bg-patria-surface border-patria-muted/30 ${g.measure && !measureOk.ok ? "border-patria-terracotta/50" : ""}`} />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-patria-muted">Deadline</span>
                  <Input type="date" value={g.deadline} onChange={(e) => setGoals((arr) => arr.map((x, j) => (j === i ? { ...x, deadline: e.target.value } : x)))} data-testid={`plan-goal-deadline-${i}`} className="bg-patria-surface border-patria-muted/30 w-auto" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-patria-ink font-medium">Scheduled check-in dates</Label>
          <Button type="button" size="sm" variant="ghost" onClick={() => setCheckins((c) => [...c, { _id: uid(), value: "" }])} className="text-patria-green" data-testid="plan-add-checkin"><Plus className="h-4 w-4 mr-1" /> Add date</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {checkins.map((c, i) => (
            <div key={c._id} className="flex items-center gap-1">
              <Input type="date" value={c.value} onChange={(e) => setCheckins((arr) => arr.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} data-testid={`plan-checkin-${i}`} className="bg-patria-surface border-patria-muted/30 w-auto" />
              {checkins.length > 1 && <button type="button" onClick={() => setCheckins((arr) => arr.filter((_, j) => j !== i))} className="text-patria-terracottaDeep"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => submit.mutate()} disabled={!valid || submit.isPending} data-testid="plan-submit" className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface">Submit plan for approval</Button>
      </div>
    </div>
  );
}

export function PlanSection({ c, me }) {
  const plan = c.plan;
  const canBuild = me.role === "manager" && ["entry_approved", "plan_rejected"].includes(c.status);
  const canDecide = ["people_ops", "leadership"].includes(me.role) && plan?.status === "submitted" && plan?.built_by?.id !== me.id;

  // Only show once entry is approved or a plan exists
  if (!plan && c.status !== "entry_approved" && c.status !== "plan_rejected") {
    return (
      <Card className="bg-patria-surface border-patria-muted/15 p-6 opacity-70" data-testid="plan-section-locked">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-patria-muted/10 flex items-center justify-center"><Target className="h-4 w-4 text-patria-muted" /></div>
          <div>
            <h3 className="font-heading font-semibold text-patria-ink">Stage 2 · COMPASS Performance Plan (30/60/90)</h3>
            <p className="text-xs text-patria-muted">Unlocks after People Operations approves entry.</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-patria-surface border-patria-muted/15 p-6" data-testid="plan-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-patria-green/10 flex items-center justify-center"><Target className="h-4 w-4 text-patria-green" /></div>
          <div>
            <h3 className="font-heading font-semibold text-patria-ink">Stage 2 · COMPASS Performance Plan</h3>
            <p className="text-xs text-patria-muted">Built by the manager, approved by People Operations before it begins.</p>
          </div>
        </div>
        {plan && <StatusChip label={plan.status === "submitted" ? "Pending approval" : plan.status === "active" ? "Active" : plan.status === "rejected" ? "Rejected" : "Completed"} tone={plan.status === "submitted" ? "amber" : plan.status === "rejected" ? "red" : "green"} testId="plan-status" />}
      </div>

      {plan ? (
        <div className="space-y-4">
          <PlanView plan={plan} />
          <ApprovalResult record={{ status: plan.status === "rejected" ? "rejected" : "approved", decided_by: plan.decided_by, decided_at: plan.decided_at, reason: plan.reason }} approvedLabel="Plan approved" rejectedLabel="Plan rejected" />
          {canDecide && <ApprovalActions caseId={c.id} endpoint={`/cases/${c.id}/plan/decision`} queryKey={["case", c.id]} label="Plan" />}
          {plan.status === "rejected" && canBuild && (
            <div className="pt-2 border-t border-patria-muted/15">
              <p className="text-sm font-medium text-patria-ink mb-3">Revise & resubmit the plan</p>
              <PlanBuilder caseId={c.id} feedback={c.feedback} />
            </div>
          )}
        </div>
      ) : (
        <PlanBuilder caseId={c.id} feedback={c.feedback} />
      )}
    </Card>
  );
}
